import React, { useState, useEffect } from "react";
import SEO from "../seo";
const ManageAppPermissionsPrivacy = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "understanding-app-permissions",
        "viewing-app-privacy-permissions",
        "revoking-app-access",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Manage App Permissions & Privacy | xPage Help Center"
        description="Learn how to manage app permissions and privacy on Xpage. Review and control what data installed apps can access, and revoke access when necessary to protect your store and customer information."
        keywords="Xpage apps, app permissions, app privacy, manage apps, revoke access, installed apps, data security"
        canonical="https://help.xpage.ai/help/apps/Manage%20App%20Permissions%20%26%20Privacy"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Manage App Permissions & Privacy 
          </h1>
          <p className="text-sm text-systemLabel">
            Xpage gives you full control over what data installed apps can
            access, ensuring your store and customer information remain secure.
            Every app must explicitly request permissions to access store,
            order, and customer data, and you can review these requests before
            installation or at any time after.
          </p>
        </header>

        {/* Understanding App Permissions Section */}
        <section
          id="understanding-app-permissions"
          className="mb-6 scroll-mt-20"
        >
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Understanding App Permissions
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Permissions define what store data apps can view or modify. This
                includes Personally Identifiable Information (PII), which refers
                to data that can uniquely identify an individual, such as
                contact details, addresses, or location information.
              </p>

              <p className="text-sm text-systemLabel">
                Types of personal data apps may access:
              </p>

              <div className="space-y-3">
                <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Customer information
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Name, email, phone number, shipping address, IP address, and
                    browser/OS data.
                  </p>
                </div>
                <div className="p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Store owner information
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Contact details including name, email, phone number, and
                    address.
                  </p>
                </div>
                <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Employee information
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Name, email, and phone number of team members.
                  </p>
                </div>
                <div className="p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Content provider information
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Details for blog authors or reviewers, such as email, IP,
                    and user agent info.
                  </p>
                </div>
              </div>

              <p className="text-sm text-systemLabel">
                Example: Apps that handle order fulfillment or shipping will
                need access to customer addresses and recent order data.
                Permissions may include:
              </p>

              <div className="space-y-3">
                <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Limited Access
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Access to recent orders (past 60 days)
                  </p>
                </div>
                <div className="p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Full Access
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Access to all order data (historical and future)
                  </p>
                </div>
              </div>

              <p className="text-sm text-systemLabel">
                Xpage reviews apps that request full access to ensure the data
                is necessary for proper functionality.
              </p>
            </div>
          </div>
        </section>

        {/* Viewing App Privacy & Permissions Section */}
        <section
          id="viewing-app-privacy-permissions"
          className="mb-6 scroll-mt-20"
        >
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Viewing App Privacy & Permissions
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                You can always check what personal data an app accesses and the
                actions it can perform:
              </p>

              <div className="space-y-3">
                <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    During installation:
                  </strong>
                  <p className="text-systemLabel text-sm">
                    Permissions and types of PII requested are clearly displayed
                    before you confirm installation.
                  </p>
                  <p className="text-systemLabel text-sm mt-1">
                    You can approve or deny access before proceeding.
                  </p>
                </div>
                <div className="p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    After installation:
                  </strong>
                  <ul className="text-systemLabel text-sm list-disc list-inside space-y-1 mt-1">
                    <li>Log in to your Xpage dashboard and navigate to Apps</li>
                    <li>
                      Find the app you want to review, click ... → View Details
                    </li>
                    <li>
                      Under Privacy Details, see the personal data the app can
                      access
                    </li>
                    <li>
                      Under Permission Details, see the actions the app is
                      authorized to perform
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Revoking App Access Section */}
        <section id="revoking-app-access" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Revoking App Access
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                If you need to remove an app's access:
              </p>

              <div className="space-y-3">
                <div className="bg-gray-400/10 p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Uninstall the app
                  </strong>
                  <p className="text-systemLabel text-sm">
                    This automatically revokes its access to your store and
                    customer data.
                  </p>
                  <p className="text-systemLabel text-sm mt-1">
                    Xpage will notify the app developer to delete any collected
                    PII within 48 hours.
                  </p>
                </div>
                <div className="p-3 rounded border border-systemBorder">
                  <strong className="text-systemTitle text-sm block mb-1">
                    Delete Customer Data
                  </strong>
                  <p className="text-systemLabel text-sm">
                    To delete data for a specific customer, Xpage will send a
                    delete request to all apps that may have accessed that
                    customer's information. For confirmation, you may contact
                    the app developer directly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#understanding-app-permissions"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "understanding-app-permissions"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Understanding App Permissions
              </a>
            </li>
            <li>
              <a
                href="#viewing-app-privacy-permissions"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "viewing-app-privacy-permissions"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Viewing App Privacy & Permissions
              </a>
            </li>
            <li>
              <a
                href="#revoking-app-access"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "revoking-app-access"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Revoking App Access
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ManageAppPermissionsPrivacy;
